import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/lib/auth";
import { useActiveEntity } from "@/lib/entity-context";
import { useFeatures } from "@/lib/features";
import { apiRequest } from "@/lib/queryClient";
import { useSupportMode } from "@/lib/support-mode";
import { useFeatureAwareness } from "@/lib/use-feature-awareness";
import { useQuery } from "@tanstack/react-query";
import {
  BarChart3,
  ClipboardList,
  Inbox as InboxIcon,
  LogOut,
  Mail,
  MessageSquare,
  MoreHorizontal,
  Settings,
  Shield,
  Zap,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "wouter";

import { MOBILE_PRIMARY_NAV_ITEMS } from "@/lib/navigation-registry";
import { prefetchRoute } from "@/lib/route-prefetch";

export function BottomNav() {
  const [location, setLocation] = useLocation();
  const { t } = useTranslation("common");
  const features = useFeatures();
  const { hasNew, markSeen, totalNewCount } = useFeatureAwareness();
  const { activeEntityId, isAllEntities } = useActiveEntity();
  const { user, logout } = useAuth();

  // OCT-10684 / DOMAIN D23: same entity keying as header-nav's badge - see the
  // comment there for why the explicit `queryFn` is required.
  const { data: inboxCountData } = useQuery<{ count: number; }>({
    queryKey: ["/api/inbox/count", isAllEntities ? "all" : activeEntityId],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/inbox/count");
      if (!res.ok) throw new Error("Failed to fetch inbox count");
      return res.json();
    },
    refetchInterval: 60000,
    staleTime: 30000,
  });
  const pendingApprovalsCount = inboxCountData?.count ?? 0;

  const { active: inSupportMode } = useSupportMode();
  const isSuperAdmin = user?.role === "superadmin" && !inSupportMode;

  const filteredPrimary = MOBILE_PRIMARY_NAV_ITEMS.filter((item) =>
    !item.featureKey || features[item.featureKey] !== false
  );

  const isActive = (url: string) => {
    if (url === "/") return location === "/";
    return location.startsWith(url);
  };

  if (isSuperAdmin) {
    return (
      <nav
        className="fixed bottom-0 left-0 right-0 bg-background border-t md:hidden z-50 pb-[env(safe-area-inset-bottom)]"
        data-testid="nav-bottom-admin"
      >
        <div className="flex items-center justify-around h-16 max-w-md mx-auto">
          <Link
            href="/admin"
            className="flex flex-col items-center justify-center gap-0.5 relative min-w-[48px] min-h-[48px] px-4 hover-elevate rounded-md text-foreground"
            data-testid="link-admin-mobile"
          >
            <span className="absolute -top-0 w-8 h-0.5 bg-foreground rounded-b-full" />
            <Shield className="w-5 h-5" strokeWidth={1.5} />
            <span className="text-[10px] font-medium tracking-wide">Admin</span>
          </Link>
          <button
            onClick={logout}
            className="flex flex-col items-center justify-center gap-0.5 relative min-w-[48px] min-h-[48px] px-4 hover-elevate rounded-md text-muted-foreground"
            data-testid="button-logout-mobile"
          >
            <LogOut className="w-5 h-5" strokeWidth={1.5} />
            <span className="text-[10px] font-medium tracking-wide">{t("nav.logout")}</span>
          </button>
        </div>
      </nav>
    );
  }

  const settingsActive = isActive("/settings");

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 bg-background border-t md:hidden z-50 pb-[env(safe-area-inset-bottom)]"
      data-testid="nav-bottom"
    >
      <div className="flex items-center justify-around h-16 max-w-md mx-auto">
        {filteredPrimary.map((item) => {
          const active = isActive(item.url);
          const muted = isAllEntities && item.entityScoped;
          return (
            <Link
              key={item.url}
              href={item.url}
              onPointerDown={() => prefetchRoute(item.url)}
              className={`flex flex-col items-center justify-center gap-0.5 relative min-w-[48px] min-h-[48px] px-2 hover-elevate rounded-md ${
                active ? "text-foreground" : "text-muted-foreground"
              } ${muted ? "opacity-50" : ""}`}
              data-testid={item.testId}
            >
              {active && <span className="absolute -top-0 w-8 h-0.5 bg-foreground rounded-b-full" />}
              <item.icon className="w-5 h-5" strokeWidth={1.5} />
              <span className="text-[10px] font-medium tracking-wide">{t(item.title)}</span>
            </Link>
          );
        })}

        <Link
          href="/settings"
          className={`flex flex-col items-center justify-center gap-0.5 relative min-w-[48px] min-h-[48px] px-2 hover-elevate rounded-md ${
            settingsActive ? "text-foreground" : "text-muted-foreground"
          }`}
          data-testid="link-settings-mobile"
        >
          {settingsActive && <span className="absolute -top-0 w-8 h-0.5 bg-foreground rounded-b-full" />}
          <Settings className="w-5 h-5" strokeWidth={1.5} />
          <span className="text-[10px] font-medium tracking-wide">{t("nav.settings")}</span>
        </Link>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              onPointerDown={() => {
                for (const r of ["/inbox", "/automations", "/reports", "/chat", "/tasks", "/communications"]) {
                  prefetchRoute(r);
                }
              }}
              className="flex flex-col items-center justify-center gap-0.5 relative min-w-[48px] min-h-[48px] px-2 hover-elevate rounded-md text-muted-foreground"
              data-testid="button-more-menu-mobile"
              aria-label={t("nav.more")}
            >
              {pendingApprovalsCount > 0 && (
                <span
                  className="absolute top-0.5 right-0.5 flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-mono font-semibold leading-none"
                  data-testid="badge-more-menu-count"
                >
                  {pendingApprovalsCount > 99 ? "99+" : pendingApprovalsCount}
                </span>
              )}
              {pendingApprovalsCount === 0 && totalNewCount > 0 && (
                <span
                  className="absolute top-0.5 right-0.5 w-2.5 h-2.5 rounded-full bg-primary animate-pulse"
                  data-testid="badge-new-features-mobile"
                />
              )}
              <MoreHorizontal className="w-5 h-5" strokeWidth={1.5} />
              <span className="text-[10px] font-medium tracking-wide">{t("nav.more")}</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            side="top"
            className="w-48 mb-2 z-[60] max-h-[60vh] overflow-y-auto"
            data-testid="menu-more-mobile"
          >
            <DropdownMenuItem
              onClick={() => setLocation("/inbox")}
              className="min-h-[44px]"
              data-testid="menu-item-inbox-mobile"
            >
              <InboxIcon className="w-4 h-4 mr-2" />
              <span className="flex-1">{t("nav.inbox")}</span>
              {pendingApprovalsCount > 0 && (
                <span
                  className="ml-auto flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-mono font-semibold leading-none"
                  data-testid="badge-pending-approvals-mobile"
                >
                  {pendingApprovalsCount > 99 ? "99+" : pendingApprovalsCount}
                </span>
              )}
            </DropdownMenuItem>
            {features.automations !== false && (
              <DropdownMenuItem
                onClick={() => {
                  markSeen("automations");
                  setLocation("/automations");
                }}
                className="min-h-[44px]"
                data-testid="menu-item-automations-mobile"
              >
                <Zap className="w-4 h-4 mr-2" />
                <span className="flex-1">{t("nav.automations")}</span>
                {hasNew("automations") && (
                  <span
                    className="ml-auto w-2 h-2 rounded-full bg-primary animate-pulse"
                    data-testid="badge-new-automations-mobile"
                  />
                )}
              </DropdownMenuItem>
            )}
            {features.aiReports !== false && (
              <DropdownMenuItem
                onClick={() => {
                  markSeen("aiReports");
                  setLocation("/reports");
                }}
                className="min-h-[44px]"
                data-testid="menu-item-reports-mobile"
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                <span className="flex-1">{t("nav.reports")}</span>
                {hasNew("aiReports") && (
                  <span
                    className="ml-auto w-2 h-2 rounded-full bg-primary animate-pulse"
                    data-testid="badge-new-reports-mobile"
                  />
                )}
              </DropdownMenuItem>
            )}
            {features.aiChat !== false && (
              <DropdownMenuItem
                onClick={() => setLocation("/chat")}
                className="min-h-[44px]"
                data-testid="menu-item-chat-mobile"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                {t("nav.chat")}
              </DropdownMenuItem>
            )}
            {features.tasks !== false && (
              <DropdownMenuItem
                onClick={() => setLocation("/tasks")}
                className="min-h-[44px]"
                data-testid="menu-item-tasks-mobile"
              >
                <ClipboardList className="w-4 h-4 mr-2" />
                {t("nav.tasks")}
              </DropdownMenuItem>
            )}
            {features.communications !== false && (
              <DropdownMenuItem
                onClick={() => {
                  markSeen("communications");
                  setLocation("/communications");
                }}
                className="min-h-[44px]"
                data-testid="menu-item-communications-mobile"
              >
                <Mail className="w-4 h-4 mr-2" />
                <span className="flex-1">{t("nav.communications")}</span>
                {hasNew("communications") && (
                  <span
                    className="ml-auto w-2 h-2 rounded-full bg-primary animate-pulse"
                    data-testid="badge-new-communications-mobile"
                  />
                )}
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </nav>
  );
}
